import { useEffect, useMemo, useState } from 'react';
import type { EChartsCoreOption } from 'echarts/core';
import { CartaoDeGrafico } from '../../design/graficos/CartaoDeGrafico';
import { CORES, Grafico, LEGENDA } from '../../design/graficos/Grafico';
import { fmt } from '../../design/graficos/modelos';
import { ErroDeArea, EstadoVazio } from '../../design/Estados';
import { diaMes, hojeBrasilia, lerMetasDaEquipe } from '../../escala';
import type { IndicadorDaEquipe, MetasDaEquipe, PessoaNasMetas, PorDoGrafico } from '../../escala';
import {
  PRESETS,
  agrupamentoSugerido,
  foraDoRanking,
  formatoDoIndicador,
  periodoDoPreset,
  rankingDoIndicador,
  situacaoDaMeta,
  variacaoPct,
} from './metas-calculo';
import type { PresetDePeriodo } from './metas-calculo';
import './metas.css';

/**
 * ABA "METAS" DO QUADRO DE HORÁRIO (dono 26/10/2026) — gráficos por colaborador: ticket médio,
 * cupons, itens, venda, margem, desconto… cada um contra a meta quando a pessoa tem meta ativa.
 * Só quem edita o quadro vê (o servidor recusa o resto). Só leitura: nada daqui grava.
 *
 *   Ranking     a equipe em ordem, num indicador (a meta de cada um ao lado)
 *   Evolução    dia a dia, semana a semana ou mês a mês, de uma pessoa ou de todas
 *   Por pessoa  um cartão por colaborador com todos os indicadores e a meta de cada um
 *
 * Sem conta ligada, sem número: aparece "sem conta ligada". Semana = segunda a domingo.
 */

const POR: readonly [PorDoGrafico, string][] = [
  ['dia', 'Dia a dia'],
  ['semana', 'Semana a semana'],
  ['mes', 'Mês a mês'],
];
const FONTE = 'Fonte: vendas do HOS por vendedor, agregadas por dia no InovaPay';

function useMetas(de: string, ate: string, por: PorDoGrafico) {
  const [d, setD] = useState<MetasDaEquipe | null>(null);
  const [erro, setErro] = useState(false);
  const [vez, setVez] = useState(0);
  useEffect(() => {
    let vivo = true;
    setErro(false);
    setD(null);
    void lerMetasDaEquipe(de, ate, por).then((r) => {
      if (!vivo) return;
      if (r.ok) setD(r.dados);
      else setErro(true);
    });
    return () => {
      vivo = false;
    };
  }, [de, ate, por, vez]);
  return { d, erro, recarregar: () => setVez((v) => v + 1) };
}

/* ================================================================== *
 * A aba
 * ================================================================== */

export function AbaMetas() {
  const hoje = hojeBrasilia();
  const [preset, setPreset] = useState<PresetDePeriodo>('mes');
  const [livre, setLivre] = useState(() => periodoDoPreset('trinta', hoje));
  const periodo = preset === 'livre' ? livre : periodoDoPreset(preset, hoje);
  const valido = periodo.de <= periodo.ate;
  const [porEscolhido, setPor] = useState<PorDoGrafico | null>(null);
  const por = porEscolhido ?? agrupamentoSugerido(periodo.de, periodo.ate);
  const { d, erro, recarregar } = useMetas(
    valido ? periodo.de : hoje,
    valido ? periodo.ate : hoje,
    por,
  );
  const [codigo, setCodigo] = useState('ticket_medio');

  const ind = d?.indicadores.find((i) => i.codigo === codigo) ?? d?.indicadores[0];

  return (
    <div className="mt">
      <div className="mt-barra">
        <div className="mt-chips" role="group" aria-label="Período">
          {PRESETS.map(([p, rotulo]) => (
            <button
              key={p}
              type="button"
              className={preset === p ? 'mt-chip mt-chip-on' : 'mt-chip'}
              aria-pressed={preset === p}
              onClick={() => {
                setPreset(p);
                setPor(null);
              }}
            >
              {rotulo}
            </button>
          ))}
        </div>
        {preset === 'livre' && (
          <div className="mt-livre">
            <label>
              De
              <input
                type="date"
                value={livre.de}
                max={hoje}
                onChange={(e) => setLivre({ ...livre, de: e.target.value })}
              />
            </label>
            <label>
              Até
              <input
                type="date"
                value={livre.ate}
                max={hoje}
                onChange={(e) => setLivre({ ...livre, ate: e.target.value })}
              />
            </label>
          </div>
        )}
        <label className="mt-por">
          Agrupar
          <select value={por} onChange={(e) => setPor(e.target.value as PorDoGrafico)}>
            {POR.map(([v, r]) => (
              <option key={v} value={v}>
                {r}
              </option>
            ))}
          </select>
        </label>
      </div>
      <p className="mt-periodo">
        {diaMes(periodo.de)} a {diaMes(periodo.ate)}
        {d?.agregadoAte && ` · números até ${diaMes(d.agregadoAte)}`} · semana de segunda a domingo
      </p>

      {!valido && <p className="mt-aviso">O início vem depois do fim do período.</p>}
      {valido && erro && <ErroDeArea oQue="as metas" aoTentar={recarregar} />}
      {valido && !erro && d === null && <p className="mt-carregando">Carregando as metas…</p>}
      {valido && d && d.pessoas.length === 0 && (
        <EstadoVazio titulo="Sem pessoas no quadro" texto="Ponha as pessoas na aba Configurar." />
      )}
      {valido && d && ind && d.pessoas.length > 0 && (
        <>
          <nav className="mt-indicadores" aria-label="Indicador">
            {d.indicadores.map((i) => (
              <button
                key={i.codigo}
                type="button"
                className={i.codigo === ind.codigo ? 'mt-chip mt-chip-on' : 'mt-chip'}
                aria-pressed={i.codigo === ind.codigo}
                onClick={() => setCodigo(i.codigo)}
              >
                {i.rotulo}
              </button>
            ))}
          </nav>
          <div className="mt-grade">
            <RankingDaEquipe d={d} ind={ind} />
            <EvolucaoDaEquipe d={d} ind={ind} />
          </div>
          <h2 className="mt-titulo">Por colaborador</h2>
          <div className="mt-pessoas">
            {d.pessoas.map((p) => (
              <PainelDaPessoa key={p.pessoaId} d={d} pessoa={p} />
            ))}
          </div>
          <p className="mt-nota">
            {FONTE}. Meta = o 1º nível da meta ativa de cada pessoa no mês. Metas de soma (vendido,
            cupons, itens) só aparecem quando o período é um mês a partir do dia 1. Pessoa sem conta
            ligada não tem número.
          </p>
        </>
      )}
    </div>
  );
}

/* ================================================================== *
 * Ranking
 * ================================================================== */

function RankingDaEquipe({ d, ind }: { d: MetasDaEquipe; ind: IndicadorDaEquipe }) {
  const f = formatoDoIndicador(ind);
  const linhas = rankingDoIndicador(d.pessoas, ind);
  const fora = foraDoRanking(d.pessoas, ind.codigo);
  const tem = linhas.length > 0;
  const comMeta = linhas.some((l) => l.meta);
  const lider = linhas[0];
  const equipe = d.equipe.valores[ind.codigo] ?? null;

  const desenhar = (modelo: 'barras' | 'colunas'): EChartsCoreOption => {
    const nomes = linhas.map((l) => l.pessoa.nome.split(' ')[0] ?? l.pessoa.nome);
    const realizado = {
      name: ind.rotulo,
      type: 'bar',
      data: linhas.map((l) => ({
        value: l.valor,
        itemStyle: {
          color:
            situacaoDaMeta(l.valor, l.meta, ind.direcao).situacao === 'bateu'
              ? '#1a7f55'
              : '#cb1b2b',
        },
      })),
      barMaxWidth: 22,
      label: {
        show: true,
        formatter: (x: { value: number }) => fmt(x.value, f),
        color: '#4b5157',
        fontSize: 11,
      },
      markLine:
        equipe === null
          ? undefined
          : {
              silent: true,
              symbol: 'none',
              lineStyle: { type: 'dashed' as const, color: '#8e959b' },
              label: { formatter: `equipe ${fmt(equipe, f)}`, color: '#6b7278', fontSize: 10 },
              data: [modelo === 'barras' ? { xAxis: equipe } : { yAxis: equipe }],
            },
    };
    const meta = comMeta
      ? {
          name: 'Meta',
          type: 'bar',
          data: linhas.map((l) => l.meta?.alvo ?? null),
          barMaxWidth: 10,
          itemStyle: { color: '#d6dadd', borderRadius: 3 },
        }
      : null;
    const valorEixo = {
      type: 'value' as const,
      axisLabel: { formatter: (v: number) => fmt(v, f), color: '#8e959b', fontSize: 11 },
      splitLine: { lineStyle: { color: '#edeff1' } },
    };
    const catEixo = {
      type: 'category' as const,
      data: nomes,
      inverse: modelo === 'barras',
      axisLabel: { color: '#4b5157', fontSize: 11 },
      axisTick: { show: false },
    };
    return {
      animationDuration: 800,
      grid: { left: 8, right: 36, top: 32, bottom: 8, containLabel: true },
      tooltip: { trigger: 'axis', valueFormatter: (v: unknown) => fmt(Number(v), f) },
      legend: { ...LEGENDA, top: 0, right: 0, show: comMeta },
      xAxis: modelo === 'barras' ? valorEixo : catEixo,
      yAxis: modelo === 'barras' ? catEixo : valorEixo,
      series: meta ? [realizado, meta] : [realizado],
    };
  };

  return (
    <CartaoDeGrafico
      id={`metas-ranking-${ind.codigo}`}
      titulo={`Ranking — ${ind.rotulo}`}
      numero={equipe === null ? null : fmt(equipe, f)}
      variacao={{
        pct: variacaoPct(equipe, d.equipe.anteriores[ind.codigo] ?? null),
        bom: ind.direcao === 'MAIOR_MELHOR' ? 'subir' : 'descer',
        base: 'período anterior',
      }}
      conclusao={
        lider ? (
          <>
            <b>{lider.pessoa.nome}</b> lidera com {fmt(lider.valor, f)}. O número grande é o da
            equipe toda.
            {(fora.semConta.length > 0 || fora.semDado.length > 0) && (
              <span className="mt-fora">
                {fora.semConta.length > 0 && ` Sem conta ligada: ${fora.semConta.join(', ')}.`}
                {fora.semDado.length > 0 && ` Sem dado no período: ${fora.semDado.join(', ')}.`}
              </span>
            )}
          </>
        ) : (
          'Ninguém tem dado neste indicador no período.'
        )
      }
      modelos={[
        ['barras', 'Barras'],
        ['colunas', 'Colunas'],
      ]}
      padrao="barras"
      desenhar={desenhar}
      fonte={FONTE}
      vazio={tem ? null : 'Sem dado deste indicador no período.'}
      altura={Math.max(240, 34 * linhas.length + 60)}
      largo
    />
  );
}

/* ================================================================== *
 * Evolução
 * ================================================================== */

function EvolucaoDaEquipe({ d, ind }: { d: MetasDaEquipe; ind: IndicadorDaEquipe }) {
  const f = formatoDoIndicador(ind);
  const [quem, setQuem] = useState<string>('todos');
  const pessoas = d.pessoas.filter((p) => !p.semConta);
  const escolhida = pessoas.find((p) => p.pessoaId === quem);
  const cats = d.blocos.map((b) => b.rotulo);
  const equipe = d.equipe.serie[ind.codigo] ?? [];
  const temPonto = (xs: readonly (number | null)[]) => xs.some((v) => v !== null);

  const desenhar = (modelo: 'linha' | 'area' | 'colunas'): EChartsCoreOption => {
    const tipo = modelo === 'colunas' ? 'bar' : 'line';
    const mostrar = escolhida
      ? [escolhida]
      : pessoas.filter((p) => temPonto(p.serie[ind.codigo] ?? []));
    const series: Record<string, unknown>[] = mostrar.map((p, i) => ({
      name: p.nome.split(' ')[0] ?? p.nome,
      type: tipo,
      data: p.serie[ind.codigo] ?? [],
      smooth: false,
      connectNulls: false,
      showSymbol: cats.length <= 31,
      symbolSize: 6,
      barMaxWidth: 22,
      color: CORES[i % CORES.length],
      areaStyle: modelo === 'area' && escolhida ? { opacity: 0.14 } : undefined,
      markLine:
        escolhida && escolhida.metas[ind.codigo]
          ? {
              silent: true,
              symbol: 'none',
              lineStyle: { type: 'dashed' as const, color: '#1a7f55' },
              label: {
                formatter: `meta ${fmt(escolhida.metas[ind.codigo]?.alvo, f)}`,
                color: '#1a7f55',
                fontSize: 10,
                position: 'insideEndTop' as const,
              },
              data: [{ yAxis: escolhida.metas[ind.codigo]?.alvo }],
            }
          : undefined,
    }));
    if (temPonto(equipe))
      series.push({
        name: 'Equipe',
        type: 'line',
        data: equipe,
        showSymbol: false,
        color: '#8e959b',
        lineStyle: { type: 'dashed' as const, width: 2 },
      });
    return {
      animationDuration: 800,
      grid: { left: 8, right: 16, top: 40, bottom: 8, containLabel: true },
      tooltip: { trigger: 'axis', valueFormatter: (v: unknown) => fmt(Number(v), f) },
      legend: { ...LEGENDA, top: 0, right: 0, type: 'scroll' },
      xAxis: {
        type: 'category',
        data: cats,
        axisLabel: { color: '#6b7278', fontSize: 11 },
        axisTick: { show: false },
      },
      yAxis: {
        type: 'value',
        axisLabel: { formatter: (v: number) => fmt(v, f), color: '#8e959b', fontSize: 11 },
        splitLine: { lineStyle: { color: '#edeff1' } },
      },
      series,
    };
  };

  const valor = escolhida
    ? (escolhida.valores[ind.codigo] ?? null)
    : (d.equipe.valores[ind.codigo] ?? null);
  const ant = escolhida
    ? (escolhida.anteriores[ind.codigo] ?? null)
    : (d.equipe.anteriores[ind.codigo] ?? null);
  const meta = escolhida?.metas[ind.codigo];
  const sit = situacaoDaMeta(valor, meta, ind.direcao);

  return (
    <CartaoDeGrafico
      id={`metas-evolucao-${ind.codigo}`}
      titulo={`Evolução — ${ind.rotulo}`}
      numero={valor === null ? null : fmt(valor, f)}
      variacao={{
        pct: variacaoPct(valor, ant),
        bom: ind.direcao === 'MAIOR_MELHOR' ? 'subir' : 'descer',
        base: 'período anterior',
      }}
      conclusao={
        escolhida
          ? meta
            ? `${escolhida.nome}: meta ${fmt(meta.alvo, f)} — ${rotuloDaSituacao(sit.situacao, sit.pct)}.`
            : `${escolhida.nome}: sem meta ativa para este indicador.`
          : 'Todas as pessoas com dado, e a linha pontilhada é a equipe somada.'
      }
      modelos={[
        ['linha', 'Linha'],
        ['area', 'Área'],
        ['colunas', 'Colunas'],
      ]}
      padrao="linha"
      desenhar={desenhar}
      fonte={FONTE}
      vazio={temPonto(equipe) ? null : 'Sem dado deste indicador no período.'}
      altura={300}
      largo
      topo={
        <label className="mt-quem">
          Quem
          <select value={quem} onChange={(e) => setQuem(e.target.value)}>
            <option value="todos">Todos juntos</option>
            {pessoas.map((p) => (
              <option key={p.pessoaId} value={p.pessoaId}>
                {p.nome}
              </option>
            ))}
          </select>
        </label>
      }
    />
  );
}

const rotuloDaSituacao = (s: ReturnType<typeof situacaoDaMeta>['situacao'], pct: number | null) =>
  s === 'bateu'
    ? 'meta batida'
    : s === 'perto'
      ? `perto da meta${pct === null ? '' : ` (${String(Math.round(pct))}%)`}`
      : s === 'abaixo'
        ? `abaixo da meta${pct === null ? '' : ` (${String(Math.round(pct))}%)`}`
        : s === 'sem_meta'
          ? 'sem meta'
          : 'sem dado';

/* ================================================================== *
 * Por pessoa (também usado na ficha)
 * ================================================================== */

/** Os indicadores que sempre aparecem no cartão da pessoa (o resto fica na aba). */
const DO_CARTAO = [
  'ticket_medio',
  'valor_vendido',
  'cupons',
  'itens',
  'itens_cupom',
  'margem_pct',
  'desconto_pct',
];

function mini(
  serie: readonly (number | null)[],
  cats: readonly string[],
  f: ReturnType<typeof formatoDoIndicador>,
  alvo: number | undefined,
  cor: string,
): EChartsCoreOption {
  return {
    animationDuration: 600,
    grid: { left: 4, right: 4, top: 6, bottom: 4 },
    tooltip: {
      trigger: 'axis',
      valueFormatter: (v: unknown) => fmt(Number(v), f),
    },
    xAxis: { type: 'category', data: cats, show: false },
    yAxis: { type: 'value', show: false, scale: true },
    series: [
      {
        type: 'line',
        data: serie,
        showSymbol: serie.length <= 14,
        symbolSize: 5,
        color: cor,
        lineStyle: { width: 2 },
        areaStyle: { opacity: 0.1 },
        markLine:
          alvo === undefined
            ? undefined
            : {
                silent: true,
                symbol: 'none',
                lineStyle: { type: 'dashed' as const, color: '#1a7f55' },
                label: { show: false },
                data: [{ yAxis: alvo }],
              },
      },
    ],
  };
}

export function PainelDaPessoa({ d, pessoa }: { d: MetasDaEquipe; pessoa: PessoaNasMetas }) {
  const cats = useMemo(() => d.blocos.map((b) => b.rotulo), [d.blocos]);
  if (pessoa.semConta)
    return (
      <section className="mt-pessoa mt-pessoa-vazia">
        <header>
          <h3>{pessoa.nome}</h3>
          <span>{pessoa.funcao ?? ''}</span>
        </header>
        <p>Sem conta ligada — sem número. Ligue a conta na ficha da pessoa (aba Dados).</p>
      </section>
    );
  const indicadores = d.indicadores.filter((i) => DO_CARTAO.includes(i.codigo));
  return (
    <section className="mt-pessoa" aria-label={`Metas de ${pessoa.nome}`}>
      <header>
        <h3>{pessoa.nome}</h3>
        <span>{pessoa.funcao ?? ''}</span>
      </header>
      <div className="mt-minis">
        {indicadores.map((i) => {
          const f = formatoDoIndicador(i);
          const v = pessoa.valores[i.codigo] ?? null;
          const meta = pessoa.metas[i.codigo];
          const sit = situacaoDaMeta(v, meta, i.direcao);
          const serie = pessoa.serie[i.codigo] ?? [];
          const cor =
            sit.situacao === 'bateu'
              ? '#1a7f55'
              : sit.situacao === 'sem_meta'
                ? '#4b5157'
                : '#cb1b2b';
          return (
            <div key={i.codigo} className={`mt-mini mt-mini-${sit.situacao}`}>
              <div className="mt-mini-topo">
                <span>{i.rotulo}</span>
                <strong>{v === null ? '—' : fmt(v, f)}</strong>
              </div>
              <div className="mt-mini-meta">
                {v === null
                  ? 'sem dado no período'
                  : meta
                    ? `meta ${fmt(meta.alvo, f)} · ${rotuloDaSituacao(sit.situacao, sit.pct)}`
                    : 'sem meta ativa'}
              </div>
              {meta && sit.pct !== null && (
                <div className="mt-barra-meta" aria-hidden="true">
                  <i
                    style={{
                      width: `${String(Math.min(100, Math.max(0, sit.pct)))}%`,
                      background: cor,
                    }}
                  />
                </div>
              )}
              {serie.some((x) => x !== null) && (
                <Grafico
                  opcao={mini(serie, cats, f, meta?.alvo, cor)}
                  altura={54}
                  rotulo={`${i.rotulo} de ${pessoa.nome} ao longo do período`}
                />
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

/** O gráfico da pessoa na ficha: o mês até hoje, dia a dia (só leitura). */
export function MetasDaPessoa({
  accountId,
  pessoaId,
}: {
  accountId: string | null;
  pessoaId: string;
}) {
  const hoje = hojeBrasilia();
  const { d, erro, recarregar } = useMetas(`${hoje.slice(0, 8)}01`, hoje, 'dia');
  const p = d?.pessoas.find((x) => x.pessoaId === pessoaId);
  if (!accountId)
    return <p className="mt-aviso">Sem conta ligada — sem número. Ligue a conta na aba Dados.</p>;
  if (erro) return <ErroDeArea oQue="as metas" aoTentar={recarregar} />;
  if (!d) return <p className="mt-carregando">Carregando…</p>;
  if (!p) return <p className="mt-aviso">Esta pessoa não está no quadro.</p>;
  return (
    <div className="mt">
      <p className="mt-periodo">
        Este mês, de {diaMes(d.de)} a {diaMes(d.ate)}
        {d.agregadoAte && ` · números até ${diaMes(d.agregadoAte)}`}
      </p>
      <PainelDaPessoa d={d} pessoa={p} />
    </div>
  );
}
